import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useIsAuthenticated } from "@azure/msal-react";

// Simple bar
import SimpleBar from "simplebar-react";

// Pro sidebar
import { ProSidebar, Menu, MenuItem, SubMenu, SidebarFooter } from "react-pro-sidebar";
import { isActiveMenu, getUserProfile, moduleAccess } from "../../helpers/functions_helper";

import MenuSearch from "./SideMenu/MenuSearch";
import { getMenuData } from "./SideMenu/menuData";
import { filterMenu } from "./SideMenu/filterMenu";

const SidebarContent = (props: any) => {
  const scrollableNodeRef = React.createRef() as any;
  const refDiv = React.createRef() as any;
  const { t } = useTranslation();
  const userProfile = getUserProfile();

  const { getSidebarcollpsed } = useSelector((state: any) => ({
    getSidebarcollpsed: state.Common.sidebarcollpsed,
  }));

  const isAzureAuthenticated = useIsAuthenticated();

  // Search state
  const [search, setSearch] = useState("");
  const menuItems = getMenuData(t, userProfile, moduleAccess, isAzureAuthenticated);
  const filteredMenu = filterMenu(menuItems, search).filter((item) => item.condition);

  // Scroll active menu into view
  const scrollElement = (item: any) => {
    setTimeout(() => {
      if (refDiv.current && item) {
        const currentPosition = item.offsetTop;
        if (currentPosition > window.innerHeight) {
          refDiv.current.getScrollElement().scrollTop = currentPosition + 300;
        }
      }
    }, 300);
  };

  useEffect(() => {
    const activeItem = document.getElementsByClassName("pro-menu-item active") as any;
    if (activeItem && activeItem[0]) {
      scrollElement(activeItem[0]);
    }
  }, [window.location.pathname]);

  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // handle submenu click
  const handleSubmenuToggle = (key: string) => {
    if (openKeys.includes(key)) {
      setOpenKeys(openKeys.filter((k) => k !== key));
    } else {
      setOpenKeys([...openKeys, key]);
    }
  };

  const highlightText = (text: string, search: string) => {
    if (!search) return text;

    const regex = new RegExp(`(${search})`, "gi"); // case-insensitive
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="menu-highlight">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const renderMenuItem = (item: any) => {
    // active child by URL
    const isChildActive =
      item.children?.some((child: any) => isActiveMenu(window.location.pathname, child.key) || child.children?.some((sub: any) => isActiveMenu(window.location.pathname, sub.key))) || false;

    // search match
    const hasSearchMatch =
      item.label.toLowerCase().includes(search.toLowerCase()) ||
      item.children?.some((child: any) => child.label.toLowerCase().includes(search.toLowerCase()) || child.children?.some((sub: any) => sub.label.toLowerCase().includes(search.toLowerCase())));

    if (item.children && item.children.length > 0) {
      const validChildren = item.children.filter((child: any) => child.condition);
      if (validChildren.length === 0) return null;

      const shouldOpen = isChildActive || (search !== "" && hasSearchMatch) || openKeys.includes(item.key);

      return (
        <SubMenu
          key={item.key}
          title={item.label}
          icon={<span className="material-symbols-outlined">{item.icon}</span>}
          open={shouldOpen}
          onClick={() => handleSubmenuToggle(item.key)} // <-- allow user toggle
        >
          {validChildren.map(renderMenuItem)}
        </SubMenu>
      );
    }

    return (
      <MenuItem key={item.key} active={isActiveMenu(window.location.pathname, item.key) || isChildActive} icon={<span className="material-symbols-outlined">{item.icon}</span>}>
        {highlightText(item.label, search)}
        {item.path && <Link to={item.path} />}
      </MenuItem>
    );
  };

  return (
    <React.Fragment>
      <SimpleBar id="simple-bar" className="h-100" scrollableNodeProps={{ ref: scrollableNodeRef }} ref={refDiv}>
        <ProSidebar collapsed={getSidebarcollpsed}>
          {/* Search input */}
          <MenuSearch onSearch={setSearch} />

          <Menu id="side-menu">
            <li className="menu-title">{t("Menu")}</li>
            {filteredMenu.map(renderMenuItem)}
          </Menu>

          {/* Footer menu */}
          {moduleAccess("all-user-manual") && (
            <SidebarFooter>
              <Menu>
                <MenuItem key="user.manual" active={isActiveMenu(window.location.pathname, "user-manual")} icon={<span className="align-middle material-symbols-outlined fw-light">help</span>}>
                  <span>{t("User manual")}</span>
                  <Link id="userManualLink" to="/user-manual/list/all" />
                </MenuItem>
              </Menu>
            </SidebarFooter>
          )}
        </ProSidebar>
      </SimpleBar>
    </React.Fragment>
  );
};

export default SidebarContent;
